import type {ReactNode} from 'react';
// @ts-expect-error
import type {Control, Watch} from 'react-hook-form';

import type {ListItem} from 'shells/draggable-row/draggable-list';

import type {EntityType} from 'nutshell-graphql-types';
import {FormFieldType} from 'nutshell-graphql-types';

import {CheckboxesField} from '../../../editor/preview/fields/checkboxes-field';
import {DateField} from '../../../editor/preview/fields/date-field';
import {DropdownField} from '../../../editor/preview/fields/dropdown-field';
import {EnumOptions} from '../../../editor/preview/fields/enum-options';
import {FileUploadField} from '../../../editor/preview/fields/file-upload-field';
import {ImageField} from '../../../editor/preview/fields/image-field';
import {LabelField} from '../../../editor/preview/fields/label-field';
import {LabelFieldWrapper} from '../../../editor/preview/fields/label-field-wrapper';
import {MultiLineAddressField} from '../../../editor/preview/fields/multi-line-address-field';
import {RadioField} from '../../../editor/preview/fields/radio-field';
import {SingleLineAddressField} from '../../../editor/preview/fields/single-line-address-field';
import {SubmitButtonField} from '../../../editor/preview/fields/submit-button-field';
import {TextareaField} from '../../../editor/preview/fields/textarea-field';
import {TextfieldField} from '../../../editor/preview/fields/textfield-field';
import {TextfieldFieldWrapper} from '../../../editor/preview/fields/textfield-field-wrapper';

import type {FormFieldSchema} from '../../../../../core/nutshell-fields/types';
import {
    getFieldDisplayType,
    getElementToFocusFromClick,
    getFieldMappingLabel,
} from '../../../../helpers';
import type {ActiveField} from '../../../../types';

import {applyCustomSingleChoiceEnumFieldDisplayTypeOverride} from '../../sidebar/add-custom-field/helpers';
import type {Field, FormFieldArrayRow} from '../../types';

import styles from '../form-editor.module.css';

const FIELD_COMPONENT_MAP = {
    INPUT: null,
    TEXT: LabelField,
    HEADER: LabelField,
    SUBHEADER: LabelField,
    textfield: TextfieldField,
    email: TextfieldField,
    phone: TextfieldField,
    singleLineAddress: SingleLineAddressField,
    textarea: TextareaField,
    dropdown: DropdownField,
    RADIO: RadioField,
    IMAGE: ImageField,
    CHECKBOXES: CheckboxesField,
    FILE: FileUploadField,
    URL: TextfieldField,
    multiLineAddress: MultiLineAddressField,
    date: DateField,
} as const;

/**
 *
 * @param {*} fieldDisplayType the value we are using to determine how to dispay the field
 * @returns Returns the component to display
 */
// @ts-expect-error
function getComponentForType(fieldDisplayType) {
    // @ts-expect-error
    return FIELD_COMPONENT_MAP[fieldDisplayType];
}

/**
 *
 * @param {*} fieldTypeValue type of the field
 * @param {*} fieldDisplayType displayType of the field
 * @returns Returns a boolean, whether the item is a textfield. A textfield has type 'INPUT' and
has a displayType that is not 'dropdown.'
 */
// @ts-expect-error
function getIsTextfield(fieldTypeValue, fieldDisplayType) {
    return fieldTypeValue === FormFieldType.INPUT && fieldDisplayType !== 'dropdown';
}

/**
 *
 * @param {*} fieldTypeValue type of the field
 * @returns  Returns a boolean, whether the item is a header.
 */
// @ts-expect-error
function getIsHeader(fieldTypeValue) {
    return (
        fieldTypeValue === FormFieldType.TEXT ||
        fieldTypeValue === FormFieldType.HEADER ||
        fieldTypeValue === FormFieldType.SUBHEADER
    );
}

/**
 *
 * @param {*} fieldTypeValue type of the field
 * @param {*} fieldDisplayType displayType of the field
 * @returns Returns a boolean, whether the item is a dropdown. 'Dropdown' display types are saved in the
database as type 'INPUT' and we rely on the displayType to determine how to display them.
 */
// @ts-expect-error
function getIsDropdown(fieldTypeValue, fieldDisplayType) {
    return fieldTypeValue === FormFieldType.INPUT && fieldDisplayType === 'dropdown';
}

/**
 *
 * @param {*} fieldTypeValue type of the field
 * @returns  Returns a boolean, whether the item should be displayed as radio buttons.
 */
// @ts-expect-error
function getIsRadio(fieldTypeValue) {
    return fieldTypeValue === FormFieldType.RADIO;
}

/**
 *
 * @param {*} fieldTypeValue type of the field
 * @returns  Returns a boolean, whether the item should be displayed as checkboxes.
 */
// @ts-expect-error
function getIsCheckboxes(fieldTypeValue) {
    return fieldTypeValue === FormFieldType.CHECKBOXES;
}

/**
 *
 * @param {*} fieldTypeValue type of the field
 * @returns  Returns a boolean, whether the item is a file upload.
 */
// @ts-expect-error
function getIsFileUpload(fieldTypeValue) {
    return fieldTypeValue === FormFieldType.FILE;
}

/**
 *
 * @param {*} fieldTypeValue type of the field
 * @returns  Returns a boolean, whether the item is a url field.
 */
// @ts-expect-error
function getIsUrl(fieldTypeValue) {
    return fieldTypeValue === FormFieldType.URL;
}

/**
 *
 * @param {*} fieldTypeValue type of the field
 * @returns  Returns a boolean, whether the item is a url field.
 */
// @ts-expect-error
function getIsImage(fieldTypeValue) {
    return fieldTypeValue === FormFieldType.IMAGE;
}

/**
 *
 * @param {*} fieldTypeValue type of the field
 * @returns  Returns a boolean, whether the item is a date field.
 */
// @ts-expect-error
function getIsDate(fieldTypeValue) {
    return fieldTypeValue === FormFieldType.DATE;
}

type PageFieldListItemInput = {
    fieldPathPrefix: string;
    pageIndex: number;
    index: number;
    field: FormFieldArrayRow;
    formSchema: FormFieldSchema;
    activeField: ActiveField | null | undefined;
    setActiveField: (arg1?: ActiveField | null | undefined) => void;
    watch: Watch;
    control: Control;
    onRemoveField: (index: number) => void;
};

function buildPageFieldListItem(input: PageFieldListItemInput): ListItem {
    const {field, index} = input;
    const fieldPath = `${input.fieldPathPrefix}.${index}`;
    // We watch for the field type to change, as it does when toggling between
    // radio and dropdown field types or eventually checkboxes and multi select dropdown
    // field types
    const fieldTypeValue = input.watch(`${fieldPath}.type`);

    if (fieldTypeValue === FormFieldType.SUBMIT_BUTTON) {
        const fontStyle = input.watch('fontStyle');
        const fontFamily = fontStyle ? fontStyle.fontFamily : undefined;
        const isActive = Boolean(input.activeField && input.activeField.id === field.id);
        return {
            id: field.key,
            component: (
                <div
                    style={{fontFamily}}
                    onClick={(e) => {
                        e.stopPropagation();
                        input.setActiveField({id: field.id});
                    }}
                    className={styles['submit-container']}
                >
                    <SubmitButtonField
                        control={input.control}
                        name={fieldPath}
                        watch={input.watch}
                    />
                </div>
            ),
            isActive,
        };
    }

    // We watch for the fieldName to change, as it does when toggling between
    // name, first name, and last name fields
    const fieldNameValue = input.watch(`${fieldPath}.fieldName`);
    const color = input.watch('inputStyle.color');
    const fontSize = input.watch('inputStyle.fontSize');

    // @ts-expect-error
    const fieldKey: string = fieldNameValue || field.attributeId || field.fieldName;
    const fieldSchemaForDisplay = input.formSchema.properties[fieldKey];

    let fieldDisplayType = getFieldDisplayType(input.formSchema, {
        ...(field as Field),
        type: fieldTypeValue,
    });
    fieldDisplayType = applyCustomSingleChoiceEnumFieldDisplayTypeOverride(
        fieldTypeValue,
        fieldSchemaForDisplay,
        fieldDisplayType
    );

    const Component = getComponentForType(fieldDisplayType);
    const isActive = Boolean(input.activeField && input.activeField.id === field.id);
    const isTextfield = getIsTextfield(fieldTypeValue, fieldDisplayType);
    const isHeader = getIsHeader(fieldTypeValue);
    const isDropdown = getIsDropdown(fieldTypeValue, fieldDisplayType);
    const isRadio = getIsRadio(fieldTypeValue);
    const isCheckboxes = getIsCheckboxes(fieldTypeValue);
    const isFileUpload = getIsFileUpload(fieldTypeValue);
    const isUrl = getIsUrl(fieldTypeValue);
    const isImage = getIsImage(fieldTypeValue);
    const isEnumType = isDropdown || isRadio || isCheckboxes;
    const isDate = getIsDate(fieldTypeValue);

    // @ts-expect-error
    const entityType: EntityType | null | undefined = field.entityType
        ? // @ts-expect-error
          field.entityType
        : undefined;
    const fieldSchema = fieldSchemaForDisplay;
    // @ts-expect-error
    const subfields = field.subfields;

    const defaultValues = input.watch(`${fieldPath}.defaultValues`);

    const isCustomCanvasField = Boolean(
        fieldSchema && 'isCustomField' in fieldSchema && fieldSchema.isCustomField
    );

    if (!Component) {
        return {
            id: field.key,
            component: null,
            isActive,
        };
    }

    let componentValue = (
        <div
            onClick={(e) => {
                input.setActiveField({
                    id: field.id,
                    element: getElementToFocusFromClick(e),
                    ...(isCustomCanvasField
                        ? {
                              customFieldEditContext: {
                                  pageIndex: input.pageIndex,
                                  fieldIndex: index,
                              },
                          }
                        : {}),
                });
            }}
            className={
                isEnumType || isTextfield || isFileUpload || isUrl || isDate
                    ? styles.field
                    : styles['header-field']
            }
        >
            <Component
                key={field.key}
                control={input.control}
                name={fieldPath}
                watch={input.watch}
                fieldSchema={fieldSchema}
                isActive={false}
                activeElement={undefined}
                subfields={subfields}
            />
        </div>
    );

    if (isActive && (isTextfield || isFileUpload || isUrl || isDate)) {
        componentValue = (
            <TextfieldFieldWrapper
                control={input.control}
                name={fieldPath}
                // @ts-expect-error
                field={field}
                onRemoveField={() => input.onRemoveField(index)}
                watch={input.watch}
                // @ts-expect-error
                fieldSchema={fieldSchema}
                fieldMappingLabel={getFieldMappingLabel(
                    input.formSchema,
                    fieldKey,
                    entityType,
                    field.label
                )}
            >
                <div className={styles.field}>
                    <Component
                        key={field.key}
                        control={input.control}
                        watch={input.watch}
                        name={fieldPath}
                        isActive={isActive}
                        activeElement={input.activeField ? input.activeField.element : undefined}
                        subfields={subfields}
                    />
                </div>
            </TextfieldFieldWrapper>
        );
    }

    if (isActive && (isHeader || isImage)) {
        componentValue = (
            <LabelFieldWrapper onRemoveField={() => input.onRemoveField(index)}>
                <div className={styles['header-field']}>
                    <Component
                        key={field.key}
                        control={input.control}
                        watch={input.watch}
                        name={fieldPath}
                        isActive={isActive}
                        activeElement={input.activeField ? input.activeField.element : undefined}
                    />
                </div>
            </LabelFieldWrapper>
        );
    }

    if (isActive && isEnumType) {
        componentValue = (
            <TextfieldFieldWrapper
                control={input.control}
                name={fieldPath}
                // @ts-expect-error
                field={{...field, fieldName: fieldSchema.label}}
                onRemoveField={() => input.onRemoveField(index)}
                watch={input.watch}
                // @ts-expect-error
                fieldSchema={fieldSchema}
                fieldMappingLabel={getFieldMappingLabel(
                    input.formSchema,
                    fieldKey,
                    undefined,
                    field.label
                )}
            >
                <div className={styles.field}>
                    <Component
                        key={field.key}
                        control={input.control}
                        watch={input.watch}
                        name={fieldPath}
                        fieldSchema={fieldSchema}
                        isActive={isActive}
                        activeElement={input.activeField ? input.activeField.element : undefined}
                    />
                    {isDropdown && fieldSchema ? (
                        <>
                            <EnumOptions
                                fieldSchema={fieldSchema}
                                // @ts-expect-error
                                watch={input.watch}
                                displayType='dropdown'
                                style={{
                                    color,
                                    fontSize: `${fontSize}px`,
                                }}
                                defaultValues={defaultValues}
                            />
                            <div className={styles.spacer} />
                        </>
                    ) : undefined}
                </div>
            </TextfieldFieldWrapper>
        );
    }

    return {
        id: field.key,
        component: componentValue,
        isActive: isActive,
    };
}

export function getPageFieldDragOverlayReactNode(input: {
    fieldPathPrefix: string;
    pageIndex: number;
    index: number;
    field: FormFieldArrayRow;
    formSchema: FormFieldSchema;
    watch: Watch;
    control: Control;
}): ReactNode {
    return buildPageFieldListItem({
        fieldPathPrefix: input.fieldPathPrefix,
        pageIndex: input.pageIndex,
        index: input.index,
        field: input.field,
        formSchema: input.formSchema,
        activeField: null,
        setActiveField: () => undefined,
        watch: input.watch,
        control: input.control,
        onRemoveField: () => undefined,
    }).component;
}

/**
 * [This function takes the following params and returns the form fields as ListItem[]
 *
 * @param {*} input
 * @param  {*} fields an array of fields with a key value that we map over to get the components
 * @param {*} formSchema form schema with data about each field
 * @param {*} activeField id of the field that is currently active on the form
 * @param {*} onSelectFieldId function to set the selectedFieldId
 * @param {*} watch function from the form library to watch for changes to fields
 * @param {*} control function from the form library to make changes to fields
 * @param {*} onRemoveField function using the form library to remove fields
 * @returns
 */
export function getPageFieldComponents(input: {
    fieldPathPrefix: string;
    pageIndex: number;
    fields: FormFieldArrayRow[];
    formSchema: FormFieldSchema;
    activeField: ActiveField | null | undefined;
    setActiveField: (arg1?: ActiveField | null | undefined) => void;
    watch: Watch;
    control: Control;
    onRemoveField: (index: number) => void;
}): ListItem[] {
    return input.fields.map(
        (field, index): ListItem =>
            buildPageFieldListItem({
                fieldPathPrefix: input.fieldPathPrefix,
                pageIndex: input.pageIndex,
                index,
                field,
                formSchema: input.formSchema,
                activeField: input.activeField,
                setActiveField: input.setActiveField,
                watch: input.watch,
                control: input.control,
                onRemoveField: input.onRemoveField,
            })
    );
}
